/**
 * Step 3, the plan: what is guaranteed and what is expected on top, when and where, who it is for, the idea (drawn in
 * every part), the money (Lykke's split, packages, platform) and when things happen. Every figure comes from
 * POST /v5/plan; the screen only arranges it.
 */
import { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { NumberField } from '@/cowork/shared/NumberField';
import type { Creative, PartId, PlanV5 } from './types';
import { addDaysIso, day, fmt, short } from './state';
import { PartIcon } from './Visuals';
import { PART_COLOR } from './parts';
import { Card, H1, H2 } from './ui';
import { IdeaSection } from './IdeaSection';
import { tile } from './styles';
import type { StepProps } from './StepsStart';

const MEDIA: PartId[] = ['ctv', 'dooh', 'disp', 'video', 'audio'];

export function PlanStep(p: StepProps & { plan: PlanV5 | null; planLoading: boolean }) {
  const { prep, cat, s, set, plan } = p;
  const outcome = cat.outcomes.find((o) => o.id === s.outcome);
  const title = (id: PartId) => cat.parts.find((x) => x.id === id)!.title;
  return (
    <>
      <H1 title="Your plan" sub={`${s.parts.length} part${s.parts.length > 1 ? 's' : ''}${outcome ? ` to ${outcome.title.toLowerCase()}` : ''}. Change anything; your order updates as you go.`} />
      <PromiseCard {...p} />

      <section className="flex flex-col gap-3">
        <H2 note={plan ? `Earliest start ${day(plan.earliest)}: ${plan.complex ? cat.complexDeliveryWeeks : cat.deliveryWeeks} weeks from the kickoff meeting.` : undefined}>When and where</H2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Market">
            <select value={s.market} onChange={(e) => set({ market: e.target.value }, { field: 'Market', value: prep.markets.find((m) => m.id === e.target.value)?.name || e.target.value })} className={inputCls}>
              {prep.markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Start">
            <input type="date" value={s.start} min={plan?.earliest} onChange={(e) => e.target.value && set({ start: e.target.value }, { field: 'Start', value: day(e.target.value) })} className={inputCls} />
          </Field>
          <Field label="Length">
            <select value={s.weeks} onChange={(e) => set({ weeks: Number(e.target.value) }, { field: 'Length', value: `${e.target.value} weeks` })} className={inputCls}>
              {[4, 6, 8, 12, 16, 26, 52].map((w) => <option key={w} value={w}>{w} weeks</option>)}
            </select>
          </Field>
        </div>
        {plan && s.start < plan.earliest && <p className="text-[12.5px] text-[#B42318]">That is before the earliest start, {day(plan.earliest)}.</p>}
      </section>

      <section className="flex flex-col gap-3">
        <H2 note="Your Brand OS audiences">Who it is for</H2>
        <div className="flex flex-wrap gap-2">
          {prep.audiences.map((a) => {
            const on = s.audienceIds.includes(a.id);
            return (
              <button key={a.id} type="button" aria-pressed={on} onClick={() => set({ audienceIds: on ? s.audienceIds.filter((x) => x !== a.id) : [...s.audienceIds, a.id] }, on ? undefined : { field: 'Audience', value: a.name })}
                className={cn('inline-flex items-center gap-2 rounded-full border-[1.5px] py-1 pl-1 pr-3.5 text-left', on ? 'border-[#be43ff] bg-[#be43ff]/[0.04] shadow-sm' : 'border-gray-200 bg-white')}>
                {a.portraitUrl ? <img src={a.portraitUrl} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="h-8 w-8 rounded-full bg-gray-200" aria-hidden />}
                <span><span className="block text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{a.name}</span><span className="block text-[11px] text-[#667085]">{a.ageRange}{a.size ? ` · ${short(a.size)} people` : ''}</span></span>
              </button>
            );
          })}
          {!prep.audiences.length && <p className="text-[13px] text-[#667085]">No audiences in your Brand OS yet. Tell Lykke who it is for.</p>}
        </div>
      </section>

      <IdeaSection {...p} />

      <Money {...p} />

      {s.parts.some((x) => MEDIA.includes(x)) && (
        <section className="flex flex-col gap-3">
          <H2>Ads for {s.parts.filter((x) => MEDIA.includes(x)).map((x) => title(x).toLowerCase()).join(', ')}</H2>
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            {([['hub', 'From your Content Hub', 'We check each file fits'], ['creators', 'Creators make it', `Production add-on, ${fmt(cat.productionPerPiece)} an ad`], ['ai', 'Generated with AI', 'You approve each one'], ['own', 'Your own files', 'Upload by the due date']] as Array<[Creative, string, string]>).map(([id, t, d]) => (
              <button key={id} type="button" aria-pressed={s.creative === id} onClick={() => set({ creative: id }, { field: 'Ads', value: t })} className={tile(s.creative === id)}>
                <span className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{t}</span><span className="text-[12px] text-[#667085]">{d}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {plan && <Timeline s={s} plan={plan} title={title} />}
    </>
  );
}

const inputCls = 'h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-[#101828] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all';
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="flex flex-col gap-1.5"><span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>{label}</span>{children}</label>
);

function PromiseCard({ prep, cat, s, plan, planLoading }: StepProps & { plan: PlanV5 | null; planLoading: boolean }) {
  if (!plan) return <Card className="text-[13px] text-[#667085]"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Pricing your plan</Card>;
  const g = plan.promise.guaranteed, e = plan.promise.expected;
  const big: Array<[string, string]> = [];
  if (g.creators) big.push([String(g.creators), 'creators, micro size']);
  if (g.posts) big.push([String(g.posts), 'posts']);
  if (g.ugcPieces) big.push([String(g.ugcPieces), 'UGC pieces, raw files included']);
  if (g.impressions) big.push([short(g.impressions), 'impressions, guaranteed by our terms with the paid parts']);
  const exp: Array<[PartId, string]> = [];
  for (const id of s.parts) {
    if (id === 'inf' && g.creators) exp.push([id, `${g.creators} creators × 2 posts`]);
    if (id === 'seed') exp.push([id, `${e.seeds} gifts, typically ${e.seedPosts} posts and stories`]);
    if (id === 'amb') exp.push([id, `about ${e.ambassadorPosts} posts over ${plan.months} months`]);
    if ((id === 'boost' || MEDIA.includes(id) || id === 'social') && e.impressionsHigh && !exp.some(([x]) => x === 'boost' || MEDIA.includes(x))) exp.push([id, `up to ${short(e.impressionsHigh)} impressions with the other paid parts`]);
    if (id === 'lift') exp.push([id, 'before and after, people who saw it vs who did not']);
  }
  const last = prep.evidence.history?.last;
  return (
    <div className={cn('grid gap-3 lg:grid-cols-[1.6fr_1fr]', planLoading && 'opacity-70')}>
      <Card>
        <div className="text-[10px] uppercase tracking-[0.08em] text-[#667085]" style={{ fontWeight: 600 }}>Guaranteed</div>
        <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
          {big.map(([n, l]) => <div key={l}><div className="text-[24px] leading-none text-[#101828] tabular-nums" style={{ fontWeight: 600 }}>{n}</div><div className="mt-1 text-[12px] text-[#667085]">{l}</div></div>)}
          {!big.length && <p className="col-span-full text-[13px] text-[#667085]">Tracking and reporting, every month.</p>}
        </div>
        {exp.length > 0 && <>
          <div className="mt-5 text-[10px] uppercase tracking-[0.08em] text-[#667085]" style={{ fontWeight: 600 }}>Expected on top</div>
          <ul className="mt-2 flex flex-col gap-1.5">
            {exp.map(([id, t]) => <li key={id} className="grid grid-cols-[22px_150px_1fr] items-center gap-2 text-[12.5px]"><PartIcon id={id} size={20} /><span className="text-[#101828]" style={{ fontWeight: 600 }}>{cat.parts.find((x) => x.id === id)!.title}</span><span className="text-[#667085]">{t}</span></li>)}
          </ul>
        </>}
        <p className="mt-4 text-[12.5px] text-[#667085]">Your order so far: <b className="text-[#101828] tabular-nums">{fmt(plan.bill.excl)} {plan.bill.currency}</b> excl. VAT</p>
      </Card>
      <Card tone="soft" className="flex flex-col gap-2">
        <div className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>Why this plan</div>
        {last ? (
          <>
            <p className="text-[12.5px] text-[#344054]">Your last campaign with us, <b>{last.name}</b>{last.start ? `, ${day(last.start)}` : ''}.</p>
            {last.impressions > 0 && <div><div className="text-[24px] text-[#101828]" style={{ fontWeight: 600 }}>{short(last.impressions)}</div><div className="text-[11.5px] text-[#667085]">impressions</div></div>}
          </>
        ) : <p className="text-[12.5px] text-[#344054]">Built from what works for brands like yours in {prep.markets.find((m) => m.id === s.market)?.name || s.market}, and the prices in the Beatly service catalogue.</p>}
        {plan.bill.warnings.map((w) => <p key={w} className="text-[12px] text-[#B54708]">{w}</p>)}
      </Card>
    </div>
  );
}

function Money({ cat, s, set, plan }: StepProps & { plan: PlanV5 | null }) {
  const [adjust, setAdjust] = useState(false);
  const budgeted = s.parts.filter((id) => cat.parts.find((x) => x.id === id)!.price === 'budget');
  const title = (id: PartId) => cat.parts.find((x) => x.id === id)!.title;
  const split = plan?.split || {};
  const total = budgeted.reduce((a, id) => a + (split[id] || 0), 0) || 1;
  const cur = plan?.bill.currency || s.currency;
  const lineOf = (id: string) => plan?.bill.lines.find((l) => l.id === id);
  return (
    <section className="flex flex-col gap-3">
      <H2 note="Prices from the Beatly service catalogue">Money</H2>
      {budgeted.length > 0 && (
        <Card className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-[260px_1fr] sm:items-end">
            <label className="flex flex-col gap-1.5">
              <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>Budget for {budgeted.map((x) => title(x).toLowerCase()).join(', ')}, {cur}</span>
              <NumberField value={s.budget} min={0} step={10000} commit="blur" onChange={(n) => n != null && set({ budget: n }, { field: 'Budget', value: `${fmt(n)} ${cur}` })} className={inputCls} />
            </label>
            <span className="text-[12px] text-[#667085]">
              {plan?.bill.belowMinimum ? <span className="text-[#B54708]">Managed parts start at {fmt(plan.bill.belowMinimum)} {cur}. </span> : null}
              Plus our {cat.agencyFeePct}% agency fee. Lykke split it on what works for this outcome.
            </span>
          </div>
          <div className="flex h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden>
            {budgeted.map((id) => <span key={id} style={{ width: `${((split[id] || 0) / total) * 100}%`, background: PART_COLOR[id] }} />)}
          </div>
          <p className="text-[12.5px] text-[#667085]"><b className="text-[#101828]">Lykke’s split:</b> {budgeted.map((id) => `${title(id)} ${fmt(split[id] || 0)}`).join(' · ')}</p>
          {budgeted.length > 1 && (
            <>
              <button type="button" onClick={() => setAdjust((x) => !x)} aria-expanded={adjust} className="inline-flex w-fit items-center gap-1 text-[12.5px] text-[#667085] hover:text-[#101828]"><ChevronDown className={cn('h-4 w-4', adjust && 'rotate-180')} aria-hidden />Adjust the split</button>
              {adjust && (
                <div className="grid gap-2">
                  {budgeted.map((id) => (
                    <label key={id} className="grid grid-cols-[22px_160px_1fr_90px] items-center gap-2 text-[12.5px]">
                      <PartIcon id={id} size={20} /><span className="text-[#101828]">{title(id)}</span>
                      <input type="range" min={1} max={10} value={s.weights[id] ?? 5} onChange={(e) => set({ weights: { ...s.weights, [id]: Number(e.target.value) } })} aria-label={`Weight for ${title(id)}`} className="accent-[#be43ff]" />
                      <span className="text-right tabular-nums text-[#667085]">{fmt(split[id] || 0)}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {(['ugc', 'seed'] as const).filter((id) => s.parts.includes(id)).map((id) => (
        <Card key={id} className="flex flex-col gap-3">
          <div className="flex items-center gap-2"><PartIcon id={id} size={26} /><span className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{title(id)} package</span></div>
          <div className="grid gap-2 sm:grid-cols-3">
            {(id === 'ugc' ? cat.packages.ugc : cat.packages.seed).map((pk) => (
              <button key={pk.id} type="button" aria-pressed={s.packages[id] === pk.id} onClick={() => set({ packages: { ...s.packages, [id]: pk.id } }, { field: `${title(id)} package`, value: pk.label })} className={tile(s.packages[id] === pk.id)}>
                <span className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{pk.label}</span>
                <span className="text-[12px] text-[#667085]">{'seeds' in pk ? `${pk.seeds} seeds` : `${pk.collabs} creators, ${pk.activations} pieces`}</span>
                <span className="text-[13.5px] tabular-nums text-[#101828]" style={{ fontWeight: 600 }}>{fmt(pk.price)} {cur}</span>
              </button>
            ))}
          </div>
        </Card>
      ))}

      {plan && (
        <Card className="flex flex-col divide-y divide-gray-100 p-0 sm:p-0">
          {s.parts.filter((id) => id === 'lift').map((id) => lineOf(id) && <Row key={id} icon={<PartIcon id={id} size={26} />} title={lineOf(id)!.label} detail={lineOf(id)!.detail} amount={lineOf(id)!.amount} />)}
          {plan.bill.platform.map((l) => <Row key={l.id} icon={l.part ? <PartIcon id={l.part} size={26} /> : <span className="h-[26px] w-[26px]" />} title={l.label} detail={l.detail} amount={l.amount} />)}
          {plan.bill.licensing && plan.bill.licensing.amount > 0 && <Row icon={<PartIcon id="boost" size={26} />} title={`Paid usage rights, ${plan.bill.licensing.months} months`} detail={`${fmt(plan.bill.licensing.amount)} of the influencer line, so the boost can run all ${s.weeks} weeks`} amount={null} />}
          <div className="flex items-baseline justify-between gap-3 px-4 py-3.5">
            <span className="text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>All parts, fee, rights and platform<span className="block text-[11.5px] text-[#667085]" style={{ fontWeight: 400 }}>excl. VAT</span></span>
            <span className="text-[20px] tabular-nums text-[#101828]" style={{ fontWeight: 600 }}>{fmt(plan.bill.excl)} {cur}</span>
          </div>
        </Card>
      )}
    </section>
  );
}

const Row = ({ icon, title, detail, amount }: { icon: React.ReactNode; title: string; detail: string; amount: number | null }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    {icon}<span className="min-w-0 flex-1"><span className="block text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{title}</span><span className="block text-[11.5px] text-[#667085]">{detail}</span></span>
    <span className={cn('text-[13.5px] tabular-nums', amount == null ? 'text-[#667085]' : 'text-[#101828]')} style={{ fontWeight: amount == null ? 500 : 700 }}>{amount == null ? 'Included' : fmt(amount)}</span>
  </div>
);

/** From the order to the end: getting ready (striped), running (solid), and the client's decisions (diamonds). */
function Timeline({ s, plan, title }: { s: StepProps['s']; plan: PlanV5; title: (id: PartId) => string }) {
  const today = new Date().toISOString().slice(0, 10);
  const t0 = Date.parse(today), t1 = Date.parse(plan.end);
  const x = (iso: string) => Math.max(0, Math.min(100, ((Date.parse(iso) - t0) / Math.max(1, t1 - t0)) * 100));
  const d = (n: number) => addDaysIso(s.start, n);
  type Seg = { from: string; to: string; kind: 'prep' | 'run' };
  const rows: Array<{ id: PartId; segs: Seg[]; decide: string[] }> = s.parts.map((id) => {
    if (id === 'inf') return { id, segs: [{ from: d(-21), to: d(-7), kind: 'prep' }, { from: d(0), to: d(6), kind: 'run' }, ...(s.weeks >= 8 ? [{ from: d(28), to: d(35), kind: 'prep' } as Seg, { from: d(35), to: d(41), kind: 'run' } as Seg] : [])], decide: [d(-28), d(-7), ...(s.weeks >= 8 ? [d(28)] : [])] };
    if (id === 'seed') return { id, segs: [{ from: d(-14), to: d(21), kind: 'run' }], decide: [d(-21)] };
    if (id === 'ugc') return { id, segs: [{ from: d(-28), to: d(-7), kind: 'prep' }], decide: [d(-14)] };
    if (id === 'boost') return { id, segs: [{ from: d(3), to: plan.end, kind: 'run' }], decide: [] };
    if (id === 'lift') return { id, segs: [{ from: d(-6), to: d(-1), kind: 'run' }, { from: addDaysIso(plan.end, -6), to: plan.end, kind: 'run' }], decide: [] };
    return { id, segs: [{ from: d(-14), to: d(0), kind: 'prep' }, { from: d(0), to: plan.end, kind: 'run' }], decide: [d(-14)] };
  });
  return (
    <section className="flex flex-col gap-3">
      <H2 note="From your order to the end">When things happen</H2>
      <Card className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="relative ml-[150px] h-5 text-[10.5px] text-[#667085]">
            <span className="absolute left-0">Order</span>
            <span className="absolute -translate-x-1/2 text-[#be43ff]" style={{ left: `${x(s.start)}%`, fontWeight: 600 }}>Start {day(s.start)}</span>
            <span className="absolute right-0">{day(plan.end)}</span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-[150px_1fr] items-center py-1.5">
              <span className="truncate pr-2 text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>{title(r.id)}</span>
              <div className="relative h-3">
                <span className="absolute inset-y-[-6px] w-px bg-[#be43ff]" style={{ left: `${x(s.start)}%` }} aria-hidden />
                {r.segs.map((g, i) => (
                  <span key={i} className="absolute inset-y-0 rounded-full" style={{ left: `${x(g.from)}%`, width: `${Math.max(1, x(g.to) - x(g.from))}%`, background: g.kind === 'run' ? PART_COLOR[r.id] : `repeating-linear-gradient(135deg, color-mix(in srgb, ${PART_COLOR[r.id]} 45%, white) 0 3px, transparent 3px 6px)` }} />
                ))}
                {r.decide.map((dt) => <span key={dt} className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#be43ff] ring-2 ring-white" style={{ left: `${x(dt)}%` }} title={`You decide, ${day(dt)}`} />)}
              </div>
            </div>
          ))}
          <p className="mt-2 text-[11.5px] text-[#667085]"><span className="mr-1 inline-block h-2 w-2 rotate-45 bg-[#be43ff]" aria-hidden />You decide: creators, then drafts or ads. Striped: getting ready.</p>
        </div>
      </Card>
    </section>
  );
}
